import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import BottomNav from "../components/BottomNav";
import AddressSheet from "../components/AddressSheet";
import { useAppState } from "../context/AppState";
import { api } from "../lib/api";
import { useLocale } from "../context/Locale";

function normalizeRestaurant(restaurant) {
  const rawName = String(restaurant.name || "Negocio");
  const feeMatch = rawName.match(/\s*(🛵\s*S\/\s*\d+(?:[.,]\d+)?)\s*$/u);
  return {
    ...restaurant,
    cleanName: feeMatch ? rawName.slice(0, feeMatch.index).trim() : rawName,
    deliveryFee: feeMatch ? feeMatch[1].replace(/\s+/g, " ") : "",
  };
}

export default function CategoryStoresPage() {
  const { t } = useLocale();
  const { address, openAddress } = useAppState();
  const [params] = useSearchParams();
  const categoryName = params.get("name") || t("category");
  const categoryImage = params.get("image") || "";
  const categoryIds = useMemo(
    () => String(params.get("categories") || "")
      .split(",")
      .map((value) => Number(value))
      .filter(Number.isFinite),
    [params],
  );
  const categoryKey = categoryIds.join(",");
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!address || !categoryIds.length) {
      setRestaurants([]);
      return;
    }

    let active = true;
    setLoading(true);
    setError("");
    api("/get-filtered-restaurants", {
      method: "POST",
      body: JSON.stringify({
        latitude: Number(address.lat),
        longitude: Number(address.lng),
        accuracy: Math.max(0, Number(address.accuracy || 0)),
        category_ids: categoryIds,
      }),
    })
      .then((data) => {
        if (active) setRestaurants((Array.isArray(data) ? data : []).map(normalizeRestaurant));
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [address, categoryKey]);

  const visibleRestaurants = useMemo(() => {
    const search = query.trim().toLocaleLowerCase("es");
    if (!search) return restaurants;
    return restaurants.filter((restaurant) =>
      `${restaurant.cleanName} ${restaurant.description || ""}`
        .toLocaleLowerCase("es")
        .includes(search),
    );
  }, [query, restaurants]);

  return (
    <main className="food-app category-results">
      <AppHeader />
      <div className="food-app__content">
        <section className="category-results__hero">
          <Link to="/food" aria-label={t("backToCategories")}>‹</Link>
          {categoryImage && <img src={categoryImage} alt="" />}
          <div>
            <small>{t("category").toLocaleUpperCase()}</small>
            <h1>{categoryName}</h1>
            <p>{t("storesNearLocation")}</p>
          </div>
        </section>

        <label className="food-search">
          <span>⌕</span>
          <input
            type="search"
            placeholder={`${t("searchIn")} ${categoryName}`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>

        {!address && (
          <section className="location-empty">
            <span>⌖</span>
            <h2>{t("chooseLocation")}</h2>
            <p>{t("categoryAddressHelp")}</p>
            <button type="button" onClick={openAddress}>{t("chooseAddress")}</button>
          </section>
        )}

        {address && (
          <section className="restaurant-section">
            <div className="restaurant-section__heading">
              <div>
                <small>{t("inYourZone")}</small>
                <h2>{loading ? t("searchingStores") : `${visibleRestaurants.length} ${t("businesses").toLocaleLowerCase()}`}</h2>
              </div>
              <button type="button" onClick={openAddress}>{t("changeZone")}</button>
            </div>

            {loading && (
              <div className="restaurant-list" aria-label={t("loadingBusinesses")}>
                {[1, 2, 3].map((item) => <div className="restaurant-skeleton" key={item} />)}
              </div>
            )}

            {!loading && error && (
              <div className="food-message food-message--error">
                <b>{t("businessesLoadError")}</b>
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && visibleRestaurants.length === 0 && (
              <div className="food-message">
                <b>{t("noCategoryBusinesses").replace("{category}", categoryName)}</b>
                <span>{t("anotherAddress")}</span>
              </div>
            )}

            {!loading && !error && visibleRestaurants.length > 0 && (
              <div className="restaurant-list">
                {visibleRestaurants.map((restaurant) => (
                  <Link
                    to={`/food/stores/${restaurant.slug}`}
                    className="restaurant-card"
                    key={restaurant.id}
                  >
                    <div className="restaurant-card__image">
                      <img src={restaurant.image} alt={restaurant.cleanName} loading="lazy" />
                      {!restaurant.is_active && <span>{t("closedNow")}</span>}
                    </div>
                    <div className="restaurant-card__body">
                      <div className="restaurant-card__title">
                        <h3>{restaurant.cleanName}</h3>
                        {restaurant.custom_featured_name && <em>{restaurant.custom_featured_name}</em>}
                      </div>
                      <p>{restaurant.description || t("discoverMenuPromotions")}</p>
                      <div className="restaurant-card__meta">
                        <span>★ {restaurant.avgRating || restaurant.rating || "0"}</span>
                        <span>◷ {restaurant.delivery_time || "20"} min</span>
                        {restaurant.deliveryFee && <b>{restaurant.deliveryFee}</b>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
      <BottomNav />
      <AddressSheet />
    </main>
  );
}
